/**
 * TodoPanel — collapsible task list above the input area
 *
 * Shows session todos (tasks tracked by the agent).
 * Auto-dismisses after all tasks are completed/cancelled.
 * Styled similar to OpenCode's session-todo-dock.
 */
import { useState, useEffect, useRef } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { TodoList } from './TodoList.js'
import type { TodoItem } from './hooks.js'

interface Props {
  todos: TodoItem[]
}

export function TodoPanel({ todos }: Props) {
  const [expanded, setExpanded] = useState(true)
  const [dismissed, setDismissed] = useState(false)
  const dismissTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const completedCount = todos.filter(t => t.status === 'completed').length
  const cancelledCount = todos.filter(t => t.status === 'cancelled').length
  const totalCount = todos.length
  const allDone = totalCount > 0 && (completedCount + cancelledCount) === totalCount

  // Auto-dismiss after all tasks are completed/cancelled
  useEffect(() => {
    if (allDone) {
      dismissTimer.current = setTimeout(() => setDismissed(true), 2000)
    } else {
      // Reset if new todos arrive or status changes back
      setDismissed(false)
      if (dismissTimer.current) clearTimeout(dismissTimer.current)
    }
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current)
    }
  }, [allDone])

  // Auto-scroll to in_progress item
  const listRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!expanded || !listRef.current) return
    const inProgress = listRef.current.querySelector('[data-status="in_progress"]')
    if (inProgress) {
      inProgress.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [expanded, todos])

  if (totalCount === 0 || dismissed) return null

  return (
    <div className={`border-t themed-border transition-all duration-300 ${
      allDone ? 'opacity-60' : ''
    }`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs themed-text-muted hover:themed-text transition-colors"
      >
        {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        <span>To-dos</span>
        <span className="themed-text-muted">
          ({completedCount}/{totalCount})
        </span>
        {/* Progress bar */}
        {totalCount > 0 && (
          <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden max-w-[100px]">
            <div
              className="h-full bg-green-500/60 rounded-full transition-all duration-500"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            />
          </div>
        )}
      </button>

      {/* Task list */}
      {expanded && (
        <div ref={listRef} className="px-3 pb-2 max-h-32 overflow-y-auto">
          <TodoList todos={todos} />
        </div>
      )}
    </div>
  )
}
